import SEO from '@/components/common/SEO';
import { Breadcrumb } from '@/components/common/Breadcrumb';
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Clock, 
  Send, 
  Loader2, 
  CheckCircle,
  MessageSquare,
  Building2,
  Globe
} from 'lucide-react';
import { contactAPI } from '@/services/api';
import { toast } from 'sonner';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [companyInfo, setCompanyInfo] = useState({
    company_name: 'GRC EDUCATORS INC',
    address: '1312 17th Street, Unit #2229',
    city: 'Denver',
    state: 'Colorado',
    zip_code: '80202',
    country: 'USA',
    phone: '713-401-9995',
    email: 'support@grceducators.com',
  });

  useEffect(() => {
    fetchCompanyInfo();
  }, []);

  const fetchCompanyInfo = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_URL}/api/public/company-info`);
      if (response.ok) {
        const data = await response.json();
        setCompanyInfo(data);
      }
    } catch (error) {
      console.error('Failed to fetch company info:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await contactAPI.submit(formData);
      setSuccess(true);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', phone: '', company: '', subject: '', message: '' });
    } catch (err) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Build full address
  const fullAddress = [
    companyInfo.address,
    [companyInfo.city, companyInfo.state].filter(Boolean).join(', '),
    companyInfo.zip_code,
    companyInfo.country
  ].filter(Boolean).join(' ');

  const contactInfoList = [
    {
      icon: Mail,
      title: 'Email',
      value: companyInfo.email,
      href: `mailto:${companyInfo.email}`,
    },
    {
      icon: Phone,
      title: 'Phone',
      value: companyInfo.phone,
      href: `tel:+1${companyInfo.phone?.replace(/-/g, '')}`,
    },
    {
      icon: MapPin,
      title: 'Location',
      value: fullAddress,
      href: null,
    },
    {
      icon: Clock,
      title: 'Hours',
      value: 'Mon-Fri: 9AM-6PM EST',
      href: null,
    },
  ];

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 pt-24 flex items-center justify-center" data-testid="contact-page">
        <Card className="max-w-md mx-4 border-0 shadow-xl">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-navy-700 mb-3">Message Sent!</h2>
            <p className="text-slate-600 mb-6">
              Thank you for reaching out. Our team will get back to you within 24 hours.
            </p>
            <Button 
              onClick={() => setSuccess(false)}
              className="bg-navy-700 hover:bg-navy-800 text-white rounded-xl"
            >
              Send Another Message
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent" data-testid="contact-page">
      <SEO
        title="Contact Us"
        description="Get in touch with GRC Educators. Questions about webinars, certifications, group training or invoices? Email support@grceducators.us or call 713-401-9995."
        path="/contact"
      />
      {/* Header - MailCraft light editorial */}
      <section className="hero-canvas border-b border-zinc-200 pt-28 pb-16">
        <div className="container-custom">
          <Breadcrumb />
          <div className="max-w-2xl">
            <span className="mono-label inline-flex items-center gap-2 text-[11px] text-zinc-600 bg-white border border-zinc-200 rounded-full px-3 py-1 mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              Contact Us
            </span>
            <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-[-0.03em] leading-[1.0] mb-4">
              <span className="text-zinc-950">Get in </span>
              <span className="text-slate-400">touch.</span>
            </h1>
            <p className="text-zinc-500 text-lg">
              Have questions about our training programs? We're here to help.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <div className="grid lg:grid-cols-5 gap-8">
              {/* Contact Info - Compact Sidebar */}
              <div className="lg:col-span-2 space-y-4">
                {contactInfoList.map((info, idx) => (
                  <Card key={idx} className="border-0 shadow-md hover:shadow-lg transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-navy-100 flex items-center justify-center flex-shrink-0">
                          <info.icon className="w-5 h-5 text-navy-600" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wider">{info.title}</p>
                          {info.href ? (
                            <a href={info.href} className="font-medium text-navy-700 hover:text-gold-600 transition-colors">
                              {info.value}
                            </a>
                          ) : (
                            <p className="font-medium text-navy-700">{info.value}</p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {/* Quick Links */}
                <Card className="border-0 shadow-md bg-navy-700 text-white">
                  <CardContent className="p-5">
                    <h3 className="font-semibold mb-3">Quick Links</h3>
                    <div className="space-y-2 text-sm">
                      <a href="/webinars" className="flex items-center gap-2 text-slate-300 hover:text-gold-400 transition-colors">
                        <Globe className="w-4 h-4" />
                        Browse Webinars
                      </a>
                      <a href="/contact?type=corporate" className="flex items-center gap-2 text-slate-300 hover:text-gold-400 transition-colors">
                        <Building2 className="w-4 h-4" />
                        Corporate Training
                      </a>
                      <a href="/join-speaker" className="flex items-center gap-2 text-slate-300 hover:text-gold-400 transition-colors">
                        <MessageSquare className="w-4 h-4" />
                        Become a Speaker
                      </a>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Contact Form - Compact */}
              <div className="lg:col-span-3">
                <Card className="border-0 shadow-xl">
                  <CardContent className="p-6 md:p-8">
                    <h2 className="font-heading text-xl font-semibold text-navy-700 mb-6">
                      Send us a message
                    </h2>
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label htmlFor="name" className="text-sm">Full Name *</Label>
                          <Input
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="John Doe"
                            required
                            className="h-10 rounded-lg"
                            data-testid="contact-name"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="email" className="text-sm">Email *</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="john@company.com"
                            required
                            className="h-10 rounded-lg"
                            data-testid="contact-email"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label htmlFor="phone" className="text-sm">Phone</Label>
                          <Input
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="+1 (555) 000-0000"
                            className="h-10 rounded-lg"
                            data-testid="contact-phone"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="company" className="text-sm">Company</Label>
                          <Input
                            id="company"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            placeholder="Company name"
                            className="h-10 rounded-lg"
                            data-testid="contact-company"
                          />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="subject" className="text-sm">Subject *</Label>
                        <Input
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          placeholder="How can we help?"
                          required
                          className="h-10 rounded-lg"
                          data-testid="contact-subject"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="message" className="text-sm">Message *</Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Tell us more about your inquiry..."
                          required
                          rows={4}
                          className="rounded-lg resize-none"
                          data-testid="contact-message"
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full h-11 bg-zinc-950 hover:bg-zinc-800 text-white font-semibold rounded-lg transition-colors"
                        data-testid="contact-submit"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="mr-2 h-4 w-4" />
                            Send Message
                          </>
                        )}
                      </Button>

                      <p className="text-xs text-slate-500 text-center">
                        By submitting, you agree to our{' '}
                        <a href="/privacy-policy" className="text-navy-600 hover:underline">Privacy Policy</a>
                      </p>
                    </form>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
